import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { useEffect } from 'react';

/**
 * Encapsulates the animated border gradient logic used in TableLayoutRow.
 * - Computes left/right insets to exclude ignored cells from the gradient border
 * - Animates a CSS custom property `--angle` using requestAnimationFrame
 */
export function useRovoGradientAnimationForTableView(
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean
) {
  useEffect(() => {
    if (!isEnabled || !ref.current) {
      return;
    }

    const tr = ref.current as HTMLElement;

    // compute insets to exclude ignored td elements for gradient
    const tds = Array.from(tr.querySelectorAll('td')) as HTMLTableCellElement[];
    const contentTds = tds.filter(
      (td) => !td.classList.contains(IGNORED_TABLE_ELEMENT_CLASS)
    );
    const firstContent = contentTds[0];
    const lastContent = contentTds[contentTds.length - 1];
    if (firstContent && lastContent) {
      const trRect = tr.getBoundingClientRect();
      const firstRect = firstContent.getBoundingClientRect();
      const lastRect = lastContent.getBoundingClientRect();
      const leftInset = Math.max(0, firstRect.left - trRect.left);
      const rightInset = Math.max(0, trRect.right - lastRect.right);
      tr.style.setProperty('--rovograd-left', `${leftInset}px`);
      tr.style.setProperty('--rovograd-right', `${rightInset}px`);
    }

    let frame = 0;

    let angle = 0;
    const animate = () => {
      angle = (angle + 1) % 360;
      tr.style.setProperty('--angle', `${angle}deg`);
      frame = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isEnabled, ref]);
}

export default useRovoGradientAnimationForTableView;
